import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';
import { AppointmentAnalyticsByDate, ProductAnalyticsDTO } from '../models/analytics-dto';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService extends __BaseService {

  static readonly analyticsPath = '/Analytics';
  static readonly appointmentsPath = `${AnalyticsService.analyticsPath}/Appointments/`;
  static readonly productsPath = `${AnalyticsService.analyticsPath}/Products/`;

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   **GET - /api/bo/Analytics/filters/
   * Ritorna i valori (date e nazioni) con cui poter filtrare/ricercare le analytics
   * @param idEvent
   * @returns
   */
  getFiltersResponse(idEvent: number): __Observable<any> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AnalyticsService.analyticsPath + "/filters/" + idEvent,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req)
      .pipe(
        __filter(_r => _r instanceof HttpResponse),
        __map((_r) => _r)
      );
  }
  getFilters(idEvent: number): __Observable<any> {
    return this.getFiltersResponse(idEvent).pipe(
      __map(_r => _r.body)
    );
  }

  /**
   **GET - /api/bo/Analytics/appointments/{idEvent}
   * Ritorna le analytics della sezione appuntamenti
   * @param idEvent
   * @param params
   * @returns
   */
  getAppointmentAnalyticsResponse(idEvent: number, params?: any): __Observable<__StrictHttpResponse<AppointmentAnalyticsByDate[]>> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params) {
      if (params.Data) __params = __params.set('Data', params.Data.toString());
      if (params.FullText) __params = __params.set('FullText', params.FullText.toString());
      if (params.Country && Array.isArray(params.Country)) {
        params.Country.forEach((country: string) => {
          __params = __params.append("Countries", country);
        });
      }
    }

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AnalyticsService.appointmentsPath + idEvent,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req)
      .pipe(
        __filter(_r => _r instanceof HttpResponse),
        __map((_r) => _r as __StrictHttpResponse<AppointmentAnalyticsByDate[]>)
      );
  }
  getAppointmentAnalytics(idEvent: number, params?: any): __Observable<AppointmentAnalyticsByDate[]> {
    return this.getAppointmentAnalyticsResponse(idEvent, params).pipe(
      __map(_r => _r.body as AppointmentAnalyticsByDate[])
    );
  }

  /**
  **GET - /api/bo/Analytics/products/{idEvent}
  * Ritorna le analytics della sezione prodotti
  * @param idEvent
  * @param params
  * @returns
  */
  getProductAnalyticsResponse(idEvent: number, params?: any): __Observable<any> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params) {
      if (params.Skip) __params = __params.set('Skip', params.Skip.toString());
      if (params.Limit) __params = __params.set('Limit', params.Limit.toString());
      if (params.isDesc) __params = __params.set('isDesc', params.isDesc.toString());
      if (params.OrderBy) __params = __params.set('OrderBy', params.OrderBy.toString());
      if (params.FullText) __params = __params.set('FullText', params.FullText.toString());
      if (typeof params.BookedForSale === "boolean") __params = __params.set('BookedForSale', params.BookedForSale.toString());
    }

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AnalyticsService.productsPath + idEvent,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req)
      .pipe(
        __filter(_r => _r instanceof HttpResponse),
        __map((_r) => _r)
      );
  }
  getProductAnalytics(idEvent: number, params?: any): __Observable<ProductAnalyticsDTO> {
    return this.getProductAnalyticsResponse(idEvent, params).pipe(
      __map(_r => _r.body as ProductAnalyticsDTO)
    );

  }

  /**
   **GET - /api/bo/Analytics/appointments/{idEvent}/{idAppointment}
   **GET - /api/bo/Analytics/products/{idEvent}/{sapCode}
   * Ritorna il dettaglio delle analytiche di un determinato appuntamento o prodotto a seconda del flag "isAppointment"
   * @param idEvent
   * @param idAnalytic
   * @param isAppointment
   * @returns
   */
  getAnalyticDetailResponse(idEvent: number, idAnalytic: string, isAppointment: boolean): __Observable<any> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let path = isAppointment ?
      AnalyticsService.appointmentsPath
      :
      AnalyticsService.productsPath;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + path + idEvent + "/" + idAnalytic,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req)
      .pipe(
        __filter(_r => _r instanceof HttpResponse),
        __map((_r) => _r)
      );
  }
  getAnalyticDetail(idEvent: number, idAnalytic: string, isAppointment: boolean): __Observable<any> {
    return this.getAnalyticDetailResponse(idEvent, idAnalytic, isAppointment).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
   **GET - /api/bo/Analytics/appointments/{idEvent}/export
   **GET - /api/bo/Analytics/products/{idEvent}/export
   * Effettua l'export delle analytics degli appuntamenti e dei prodotti a seconda del flag "isAppointmentExport"
   * @param idEvent
   * @param isAppointmentExport
   * @returns
   */
  exportResponse(idEvent: number, isAppointmentExport: boolean): __Observable<__StrictHttpResponse<any>> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let path = isAppointmentExport ?
      AnalyticsService.appointmentsPath
      :
      AnalyticsService.productsPath;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + path + idEvent + "/export",
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'blob'
      });

    return this.http.request<any>(req)
      .pipe(
        __filter(_r => _r instanceof HttpResponse),
        __map((_r) => _r as __StrictHttpResponse<any>)
      );
  }
  export(idEvent: number, isAppointmentExport: boolean = false): __Observable<any> {
    return this.exportResponse(idEvent, isAppointmentExport).pipe(
      __map(_r => _r.body as any)
    );
  }



}
